import { LoginForm } from "@/components/auth/login-form";
import { Logo } from "@/components/layout/logo";
import { useAppContext } from "@/providers";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";

// ─── NCircle brand symbol (shared inline SVG) ───────────────────────────────
function BrandSymbol({ size = 48 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 258 255"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M114.01 193.952C114.01 157.612 94.47 124.092 62.86 106.172L10.06 76.2416C3.59 91.6116 0 108.502 0 126.232C0 140.592 2.35 154.412 6.69 167.312C8.41 172.422 14.03 175.112 19.13 173.382C38.73 166.732 58.31 160.412 59.88 159.592C65.97 157.482 70.58 165.462 65.79 169.682L33.72 198.372C29.7 201.962 29.27 208.172 32.87 212.192C53.35 235.062 81.88 250.572 114.01 254.272V193.952Z"
        fill="#0467C4"
      />
      <path
        d="M79.37 80.7916C110.23 98.2816 148.01 98.2816 178.87 80.7916L232.94 50.1416C214.59 25.0916 187.42 6.93158 155.99 0.211585C150.66 -0.928415 145.44 2.66158 144.43 8.01158C140.26 30.0216 135.31 53.7316 135.24 55.6616C134.06 62.0016 124.85 62.0616 123.55 55.8116L113.19 7.81158C112.06 2.56158 106.9 -0.888415 101.65 0.241585C70.37 6.97159 43.31 25.0516 25 49.9616L79.37 80.7916Z"
        fill="#FFA500"
      />
      <path
        d="M192.87 169.692C188.03 165.492 192.82 157.432 198.78 159.602L238.69 173.272C243.81 175.022 249.45 172.342 251.17 167.212C255.48 154.342 257.82 140.572 257.82 126.252C257.82 108.602 254.26 91.7816 247.84 76.4516L195.38 106.172C163.77 124.092 144.23 157.622 144.23 193.952V254.202C176.34 250.392 204.83 234.802 225.24 211.852C228.8 207.842 228.38 201.672 224.4 198.082C209.17 184.372 194.34 170.642 192.87 169.692Z"
        fill="#ACE405"
      />
    </svg>
  );
}

export default function LoginPage() {
  const { session, locale, setLocale } = useAppContext();
  const navigate = useNavigate();
  const isRTL = locale === "ar";
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  useDocumentTitle(`${t(locale, "login")} | NCircle`);

  useEffect(() => {
    if (session.status === "authenticated") {
      void navigate("/dashboard", { replace: true });
    }
  }, [session.status, navigate]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (
    session.status === "idle" ||
    session.status === "authenticating" ||
    session.status === "authenticated"
  ) {
    return null;
  }

  return (
    <div className="min-h-screen flex bg-background">
      {/* ─── Visual panel (lg+) ──────────────────────────────────────── */}
      <aside className="hidden lg:flex lg:w-5/12 xl:w-[44%] relative bg-neutral-900 text-neutral-100 overflow-hidden">
        {/* concentric line decoration */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[640px] h-[640px] rounded-full border border-white/5" />
          <div className="absolute w-[480px] h-[480px] rounded-full border border-white/[0.07]" />
          <div className="absolute w-[320px] h-[320px] rounded-full border border-white/10" />
        </div>

        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
          {/* Top: wordmark + eyebrow */}
          <div className="space-y-3">
            <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.25em] text-white/60">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
              SIGN IN · NCIRCLE
            </span>
            <div className="flex items-center gap-3">
              <BrandSymbol size={36} />
              <span className="font-display font-bold text-2xl tracking-tight">
                أثر · NCircle
              </span>
            </div>
          </div>

          {/* Center: editorial display */}
          <div className="space-y-6 max-w-md">
            <span className="inline-block text-[11px] font-sans tracking-[0.25em] text-white/40">
              {isRTL ? "للجهات المبادرة" : "FOR ENABLERS"}
            </span>
            {isRTL ? (
              <h2 className="font-display font-bold leading-[1.05] text-4xl xl:text-5xl tracking-tight">
                أثرٌ يُقاس
                <br />
                في كلِّ
                <span className="relative mx-2 inline-block">
                  <span className="relative z-10">مبادرة</span>
                  <span
                    className="absolute inset-x-0 bottom-1 h-3 bg-success/70 -z-0"
                    aria-hidden
                  />
                </span>
                .
              </h2>
            ) : (
              <h2 className="font-english font-bold leading-[1.05] text-4xl xl:text-5xl tracking-tight">
                Measurable impact
                <br />
                in every
                <span className="relative mx-2 inline-block">
                  <span className="relative z-10">initiative</span>
                  <span
                    className="absolute inset-x-0 bottom-1 h-3 bg-success/70 -z-0"
                    aria-hidden
                  />
                </span>
                .
              </h2>
            )}
            <p className="text-white/65 text-[15px] leading-relaxed">
              {isRTL
                ? "صمّم مبادرتك. اضبط أهدافك. خصّص ميزانية المكافآت — وراقب الأثر الفعلي."
                : "Design your initiative. Set your goals. Allocate reward budgets — and watch the real impact."}
            </p>
          </div>

          {/* Bottom: pillar dots row */}
          <div className="flex items-center gap-6 pt-8 border-t border-white/10">
            <PillarDot color="bg-success" label={isRTL ? "الخضراء" : "Green"} />
            <PillarDot color="bg-secondary" label={isRTL ? "الاجتماعي" : "Social"} />
            <PillarDot color="bg-primary" label={isRTL ? "المجتمعي" : "Community"} />
          </div>
        </div>
      </aside>

      {/* ─── Form panel ─────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col">
        {/* Top bar: logo + lang toggle */}
        <header className="border-b border-border">
          <div className="px-6 md:px-10 lg:px-12 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="md" />
            </Link>
            <div className="flex items-center gap-3 relative" ref={langRef}>
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="text-sm font-medium text-foreground-secondary hover:text-foreground transition-colors px-3 py-1.5 rounded-lg hover:bg-muted"
              >
                {locale === "ar" ? "EN" : "عربي"}
              </button>
              {isLangOpen && (
                <div className="absolute top-full mt-2 end-0 w-32 bg-card rounded-lg shadow-lg border border-border py-1 z-50 animate-slide-in-top">
                  <button
                    onClick={() => {
                      setLocale("ar");
                      setIsLangOpen(false);
                    }}
                    className={cn(
                      "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                      locale === "ar"
                        ? "text-primary font-bold"
                        : "text-foreground-secondary",
                    )}
                  >
                    العربية
                  </button>
                  <button
                    onClick={() => {
                      setLocale("en");
                      setIsLangOpen(false);
                    }}
                    className={cn(
                      "w-full text-start px-4 py-2 text-sm hover:bg-muted transition-colors",
                      locale === "en"
                        ? "text-primary font-bold"
                        : "text-foreground-secondary",
                    )}
                  >
                    English
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-6 md:px-10 py-10">
          <div className="w-full max-w-[440px] space-y-8">
            {/* Eyebrow + headline */}
            <div className="space-y-3">
              <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] text-foreground-secondary border border-border rounded-full px-3 py-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                {isRTL ? "تسجيل الدخول" : "SIGN IN"}
              </span>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-foreground tracking-tight leading-[1.1]">
                {isRTL ? "حيّاك مرة أخرى." : "Welcome back."}
              </h1>
              <p className="text-foreground-secondary text-[15px] leading-relaxed">
                {isRTL
                  ? "ادخل لتكمل ما بدأت — لوحتك جاهزة وتنتظرك."
                  : "Sign in to pick up where you left off — your dashboard is ready."}
              </p>
            </div>

            <LoginForm />

            {/* Bottom note: register link */}
            <div className="pt-6 border-t border-border text-center">
              <p className="text-[14px] text-foreground-secondary">
                {isRTL
                  ? "ما عندك حساب بعد؟"
                  : "Don't have an account yet?"}
                <Link
                  to="/auth/initiative/request"
                  className="font-bold text-primary hover:underline ms-2"
                >
                  {isRTL ? "أنشئ مبادرتك" : "Launch an initiative"}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Mini footer */}
        <div className="border-t border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between text-[11px] font-sans tracking-[0.2em] text-foreground-secondary">
          <span>
            © {new Date().getFullYear()} NCIRCLE ·{" "}
            {isRTL ? "جميع الحقوق محفوظة" : "ALL RIGHTS RESERVED"}
          </span>
          <span className="hidden md:inline">EST. 2026 / المدينة المنورة</span>
        </div>
      </main>
    </div>
  );
}

function PillarDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] text-white/70">
      <span className={cn("inline-block w-1.5 h-1.5 rounded-full", color)} />
      {label.toUpperCase()}
    </span>
  );
}
